import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import CameraErrorComponent from "src/components/errors/CameraErrorComponent";
import { applicationStore } from "src/store/ApplicationStore";
import * as tf from "@tensorflow/tfjs";
import { IdentificationTypes } from "src/enums/IdentificationTypes";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import logger from "src/utils/logger";
import { toast } from "react-toastify";
import { detect } from "src/utils/detect";
import LoadingComponent from "src/components/ui/LoadingComponent";
import { UploadSorrell } from "src/assets/images/svg/Upload";
import { imageStorage } from "src/utils/imageStorage";
import UploadProcessingWithAnimationComponent from "src/components/UploadProcessingWithAnimationComponent";
import Preloader from "src/components/ui/Preloader";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { useTranslation } from "react-i18next";
import {
    COUNTRIES_WITH_CUSTOM_MODEL,
    COUNTRY_MODEL_MAP,
    DEFAULT_COUNTRY_KEY,
} from "src/constants/countryConfig";
import { MAX_FILE_SIZE_MB } from "src/constants/fileConfig";
import {
    buildFileReaderTrackingPayload,
    trackSmartlookEvent,
} from "src/utils/smartlook";

const NoCameraError = observer(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const props = {
        title: t('cameraError.noCamera.title'),
        header: t('cameraError.noCamera.header'),
        previousPage: applicationStore.previousRoute.cameraError,
    };

    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [modelLoading, setModelLoading] = useState({
        loading: true,
        progress: 0,
    });
    const [model, setModel] = useState({
        net: null,
        inputShape: [1, 0, 0, 3],
    });

    const hiddenFileInputFront = useRef(null);
    const hiddenFileInputBack = useRef(null);
    const imageRef = useRef(null);
    const canvasRef = useRef(null);

    const modelName = "yolov8n";
    const isBack = applicationStore.documentSurface.front;
    const surface = !applicationStore.documentSurface.front ? "front" : "back";
    const docs_type = applicationStore.docsType;

    const handleFileClick = (event, type) => {
        if (type === "front" && hiddenFileInputFront.current) {
            hiddenFileInputFront.current.click();
        } else if (type === "back" && hiddenFileInputBack.current) {
            hiddenFileInputBack.current.click();
        }
    };

    const handleSubmit = async (frontImageFile, backImageFile) => {
        setLoading(true);

        let formObject = {
            docs_type,
            country: applicationStore.country.toUpperCase(),
        };
        formObject.docs_type = docs_type;
        if (frontImageFile) {
            formObject.id_frontimage = frontImageFile;
            await imageStorage.saveImage('img', frontImageFile);
        }
        if (backImageFile) {
            if (
                applicationStore.docsType === IdentificationTypes.IDENTITY_CARD ||
                applicationStore.docsType === IdentificationTypes.RESIDENCE_PERMIT
            ) {
                formObject.id_frontimage = await imageStorage.getImage('img');
            }
            formObject.id_backimage = backImageFile;
            await imageStorage.saveImage('backImg', backImageFile);
        }

        try {
            if (
                (applicationStore.docsType === IdentificationTypes.IDENTITY_CARD ||
                    applicationStore.docsType === IdentificationTypes.RESIDENCE_PERMIT) &&
                applicationStore.documentSurface.front &&
                !applicationStore.documentSurface.back &&
                surface === "back"
            ) {
                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        ...formObject,
                    },
                    action_index: applicationStore.workflowNextServiceResponse.index,
                    constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry },
                });
                if (response.data?.service_results[0].status !== 200) {
                    const rawMessage = response.data?.service_results[0]?.message;
                    const parsedMessage = parseServiceErrorMessage(rawMessage);
                    throw new Error(parsedMessage);
                }
                applicationStore.setWorkflowNextServiceResponse(response.data);

            } else if (
                applicationStore.docsType !== IdentificationTypes.IDENTITY_CARD &&
                applicationStore.docsType !== IdentificationTypes.RESIDENCE_PERMIT
            ) {
                const response = await workflowStepIterator({
                    access_token: applicationStore.access_token,
                    node_id: applicationStore.workflowNextServiceResponse.node_id,
                    body: {
                        ...formObject,
                    },
                    action_index: applicationStore.workflowNextServiceResponse.index,
                    constextOfStep: { step: NodeTypes.VERIFICATION, "retry": applicationStore.workflowNextServiceResponse.retry },
                });
                if (response.data?.service_results[0].status !== 200) {
                    const rawMessage = response.data?.service_results[0]?.message;
                    const parsedMessage = parseServiceErrorMessage(rawMessage);
                    throw new Error(parsedMessage);
                }
                applicationStore.setWorkflowNextServiceResponse(response.data);

            }
            if (!applicationStore.documentSurface.front && surface === "front") {
                applicationStore.setDocumentSurface({
                    ...applicationStore.documentSurface,
                    front: true,
                });
                if (
                    applicationStore.documentSurface.front &&
                    applicationStore.documentSurface.back
                ) {
                    navigate("/processing-id", { replace: true });
                    return;
                }
                navigate("/error-no-camera", { replace: true });
            }
            if (
                applicationStore.documentSurface.front &&
                !applicationStore.documentSurface.back &&
                surface === "back"
            ) {
                applicationStore.setDocumentSurface({
                    ...applicationStore.documentSurface,
                    back: true,
                });
                navigate("/processing-id", { replace: true });
                return;
            }
        } catch (error) {
            logger.error(error);
            toast.error(error?.message || t('upload.uploadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (file, type) => {
        if (!file) {
            return;
        }

        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            toast.warning(t('upload.fileSizeExceeds', { size: MAX_FILE_SIZE_MB }));
            return;
        }

        const reader = new FileReader();
        reader.onload = async () => {
            const base64Data = reader.result;
            if (typeof base64Data !== "string") {
                trackSmartlookEvent(
                    "verification_file_reader_null_result",
                    buildFileReaderTrackingPayload({
                        selectedFile: file,
                        reader,
                        status: "null_result",
                        page: "error-no-camera",
                        surface,
                        eventSource: "verification_upload_no_camera",
                    })
                );
                logger.error("Failed to read selected file as data URL");
                toast.error(t('upload.uploadFailed'));
                return;
            }

            logger.log(base64Data);

            const originalSizeInKB = (base64Data.length * 3) / 4 / 1024;
            logger.log(`Original image size: ${originalSizeInKB.toFixed(2)} KB`);
            trackSmartlookEvent(
                "verification_file_reader_success",
                {
                    ...buildFileReaderTrackingPayload({
                        selectedFile: file,
                        reader,
                        status: "success",
                        page: "error-no-camera",
                        surface,
                        eventSource: "verification_upload_no_camera",
                    }),
                    base64_length: base64Data.length,
                    original_size_kb: Number(originalSizeInKB.toFixed(2)),
                }
            );

            try {
                if (
                    type === "front" &&
                    (docs_type !== IdentificationTypes.IDENTITY_CARD || docs_type !== IdentificationTypes.RESIDENCE_PERMIT)
                ) {
                    await handleSubmit(base64Data, null);
                } else {
                    await handleSubmit(null, base64Data);
                }
            } catch (error) {
                logger.error("Error handling image:", error);
            }
        };
        reader.onerror = (error) => {
            trackSmartlookEvent(
                "verification_file_reader_error",
                {
                    ...buildFileReaderTrackingPayload({
                        selectedFile: file,
                        reader,
                        status: "error",
                        page: "error-no-camera",
                        surface,
                        eventSource: "verification_upload_no_camera",
                    }),
                    error_message: error?.type || error?.message || null,
                }
            );
            logger.error("Error reading selected file", error);
            toast.error(t('upload.uploadFailed'));
        };
        reader.onabort = () => {
            trackSmartlookEvent(
                "verification_file_reader_abort",
                buildFileReaderTrackingPayload({
                    selectedFile: file,
                    reader,
                    status: "abort",
                    page: "error-no-camera",
                    surface,
                    eventSource: "verification_upload_no_camera",
                })
            );
            logger.warn("File reading was aborted");
            toast.error(t('upload.uploadFailed'));
        };
        reader.readAsDataURL(file);
    };

    const handleOnLoad = async () => {
        setIsUploading(true);

        if (!hasCountryModel && isBack) {
            handleFileChange(file, isBack ? "back" : "front");
            setIsUploading(false);
            return;
        }

        const imageDetectResult = await detect(
            imageRef.current,
            model,
            canvasRef.current,
            { process: "uploadPhoto", country: applicationStore.country }
        );
        logger.log(imageDetectResult);

        if (imageDetectResult === undefined || imageDetectResult === null) {
            toast.warning(t('upload.documentNotValid'));
            setIsUploading(false);
            return;
        }

        if (imageDetectResult.klass === "not_valid") {
            toast.warning(t('upload.documentNotValid'));
            setIsUploading(false);
            return;
        }

        let countryName;
        if (applicationStore.country === "gb") {
            countryName = "uk";
        } else {
            countryName = applicationStore.country;
        }

        let modelName = COUNTRIES_WITH_CUSTOM_MODEL.includes(
            applicationStore.country
        )
            ? `${countryName}_${applicationStore.modelCotrolName}${isBack ? "_back" : ""}`
            : applicationStore.modelCotrolName;

        if (imageDetectResult.klass !== modelName) {
            toast.warning(t('upload.documentDoesNotMatch'));
            setIsUploading(false);
            return;
        }
        handleFileChange(file, isBack ? "back" : "front");
        setIsUploading(false);
    };

    const hasCountryModel = !!COUNTRY_MODEL_MAP[applicationStore.country];

    useEffect(() => {
        const modelType = `_${COUNTRY_MODEL_MAP[applicationStore.country] || DEFAULT_COUNTRY_KEY}`;
        tf.ready().then(async () => {
            const yolov8 = await tf.loadGraphModel(
                `${window.location.origin}/${modelName}_web_model${modelType}/model.json`,
                {
                    onProgress: (fractions) => {
                        setModelLoading({ loading: true, progress: fractions });
                    },
                }
            );

            const dummyInput = tf.ones(yolov8.inputs[0].shape);
            const warmupResults = yolov8.execute(dummyInput);

            setModelLoading({ loading: false, progress: 1 });
            setModel({
                net: yolov8,
                inputShape: yolov8.inputs[0].shape,
            });

            tf.dispose([warmupResults, dummyInput]);
        });
    }, []);

    return (
        <>
            {modelLoading.loading ? (
                <div className="custom-container">
                    <LoadingComponent />
                </div>
            ) : (
                <>
                    {loading ? (
                        <UploadProcessingWithAnimationComponent
                            title={t('upload.processing')}
                            message={t('upload.processingMessage')}
                        />
                    ) : (
                        <CameraErrorComponent {...props}>
                            {window?.top === window?.self && <Button
                                onClick={() => {
                                    applicationStore.setContinueOnAnotherDevice(
                                        "/error-no-camera"
                                    );
                                    return navigate("/continue-on-another-device", {
                                        replace: true,
                                    });
                                }}
                                className=""
                                variant="primaryWithoutIcon"
                            >
                                <span className="ms-2">{t('verification.continueAnotherDevice')}</span>
                            </Button>}

                            {applicationStore.previousRoute.cameraError === "/take-a-photo" ? (<>
                                <div className="group">
                                    {isUploading ? (
                                        <div className="w-full h-[52px] flex items-center justify-center">
                                            <Preloader variant="sorrell" />
                                        </div>
                                    ) : (
                                        <Button
                                            className={`group-hover:text-Gray50 group-hover:bg-SorrellBrown4000`}
                                            variant="secondaryWithIcon"
                                            onClick={(e) => {
                                                handleFileClick(e, isBack ? "back" : "front");
                                            }}
                                        >
                                            <UploadSorrell />
                                            <span className="ms-2 me-1 group-hover:text-Gray50 leading-5">{t('verification.uploadPhoto')} {isBack && t('verification.forBack')}</span>
                                        </Button>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/jpeg, image/jpg, image/png"
                                        onChange={(e) => {
                                            const selectedFile = e.target.files[0];
                                            if (!selectedFile) return;
                                            setFile(selectedFile);
                                            const fileSizeMB = selectedFile.size / (1024 * 1024);
                                            if (fileSizeMB > MAX_FILE_SIZE_MB) {
                                                toast.warning(t('addressVerification.fileTooLarge', { size: MAX_FILE_SIZE_MB }));
                                                return;
                                            }
                                            const url = URL.createObjectURL(selectedFile);
                                            imageRef.current.src = url;
                                        }}
                                        ref={isBack ? hiddenFileInputBack : hiddenFileInputFront}
                                        style={{ display: "none" }}
                                    />
                                </div>
                                <div
                                    className="min-w-screen min-h-screen"
                                    style={{ display: "none" }}
                                >
                                    <img
                                        alt=""
                                        src={undefined}
                                        data-smartlook-block
                                        ref={imageRef}
                                        onLoad={() => {
                                            handleOnLoad();
                                        }}
                                    />
                                    <canvas
                                        width={model.inputShape[1]}
                                        height={model.inputShape[2]}
                                        ref={canvasRef}
                                    />
                                </div>
                            </>
                            ) : (
                                <></>
                            )}
                        </CameraErrorComponent>
                    )}
                </>
            )}
        </>
    );
});

export default NoCameraError;
